import React from 'react';
import { Box, FormControl, RadioGroup, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { FormLabel, RadioOption } from './AddProductForm';

const useStyles = makeStyles(() => ({
  formGroup: {
    marginBottom: '30px',
  },
  formLabel: {
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '18px',
    letterSpacing: '0.15px',
    color: '#464B52',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '2px 0px',
    gap: '4px',
  },
  badge: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 700,
    color: '#252A2E',
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    marginLeft: '12px',
    fontFamily: "'Open Sans'",
    lineHeight: '16px',
    height: '20px',
  },
  openBadge: {
    backgroundColor: '#006638',
    color: '#FFFFFF',
    borderRadius: '12px',
  },
  closedBadge: {
    backgroundColor: '#252A2E',
    color: '#FFFFFF',
    borderRadius: '13px',
  },
  radioControl: {
    '& .MuiRadio-root': {
      padding: '4px',
    },
    '& .MuiSvgIcon-root': {
      borderRadius: '50%',
    },
    '& .Mui-checked': {
      '& .MuiSvgIcon-root': {
        color: '#217CBB',
      }
    }
  },
}));

// Type definitions
export type ApprovalType = 'open' | 'closed';

interface ApprovalOption {
  value: ApprovalType;
  label: React.ReactNode;
  description: React.ReactNode;
  badge: React.ReactNode;
}

interface ApprovalFlowSelectorProps {
  approvalFlow: ApprovalType;
  onApprovalFlowChange: (approvalFlow: ApprovalType) => void;
}

export const ApprovalFlowSelector: React.FC<ApprovalFlowSelectorProps> = ({ 
  approvalFlow, 
  onApprovalFlowChange 
}) => {
  const classes = useStyles();

  const getApprovalBadge = (type: ApprovalType) => {
    const badgeConfig: Record<ApprovalType, {class: string, label: string}> = {
      'open': { class: classes.openBadge, label: 'OPEN' },
      'closed': { class: classes.closedBadge, label: 'CLOSED' }
    };
    
    const config = badgeConfig[type];
    return <span className={`${classes.badge} ${config.class}`}>{config.label}</span>;
  };

  const approvalOptions: ApprovalOption[] = [
    {
      value: 'open',
      label: <FormattedMessage defaultMessage="Open" />,
      description: <FormattedMessage defaultMessage="No Approval request needed for subscribing this Product" />,
      badge: getApprovalBadge('open'),
    },
    {
      value: 'closed',
      label: <FormattedMessage defaultMessage="Closed" />,
      description: <FormattedMessage defaultMessage="We will approve the subscription on providing valid reasons" />,
      badge: getApprovalBadge('closed'),
    },
  ];

  const handleApprovalFlowChange = (newApprovalFlow: string) => {
    onApprovalFlowChange(newApprovalFlow as ApprovalType);
  };

  return (
    <Box className={classes.formGroup}>
      <Box className={classes.formLabel}>
        <FormLabel>
          <FormattedMessage defaultMessage="Subscription Approval Flow" />
        </FormLabel>
      </Box>
      <FormControl component="fieldset" style={{ marginTop: '20px', width: '100%' }}>
        <RadioGroup
          value={approvalFlow}
          onChange={(e) => handleApprovalFlowChange(e.target.value)}
        >
          {approvalOptions.map((opt, idx) => (
            <React.Fragment key={opt.value}>
              {idx !== 0 && <Divider style={{marginBottom: '10px'}} />}
              <Box>
                <Box>
                  <RadioOption
                    checked={approvalFlow === opt.value}
                    onChange={() => handleApprovalFlowChange(opt.value)}
                    value={opt.value}
                    label={opt.label}
                    description={opt.description}
                    badge={opt.badge}
                    radioClass={classes.radioControl}
                  />
                </Box>
              </Box>
            </React.Fragment>
          ))}
        </RadioGroup>
      </FormControl>
      <Divider />
    </Box>
  );
};

export default ApprovalFlowSelector;
